import { createRegistry } from '../registry.js';
import { DEFAULT_COLUMN_WIDTH } from './types.js';
export const columnTypeRegistry = createRegistry('column-type-registry', (definition) => definition.type);
/**
 * Aliases exist because monday's own enum carries two names for one column: the docs table calls
 * the deprecated single-person column `person` while the enum and the page's matrix say
 * `item_assignees`, and both are in the 43-value enum `[09§A.7 deprecated]`. An alias resolves to
 * the same definition, so nothing outside the type's folder branches on the spelling.
 */
const aliases = new Map();
export function registerColumnTypeAlias(alias, type) {
    if (columnTypeRegistry.has(alias)) {
        throw new Error(`column-type-registry: "${alias}" is a registered type, not an alias`);
    }
    aliases.set(alias, type);
}
export function columnTypeAliases() {
    return aliases;
}
/**
 * The fall-back for a monday column type this build does not model yet (M1-B5 acceptance R1).
 * Set once by `registerBuiltInColumnTypes()`; kept here rather than imported so the registry
 * module has no dependency on any single type's folder.
 */
let fallback;
export function setUnsupportedColumnType(definition) {
    fallback = definition;
}
/**
 * Resolves a column type. An **unregistered** type resolves to the `unsupported` definition,
 * which round-trips `value` untouched, rather than throwing: one unmodelled column must never
 * take a whole board read or an import down (M1-B5 acceptance R1, §10 case 11). Use
 * `hasColumnType` when the caller needs to know the difference.
 */
export function getColumnType(type) {
    const resolved = aliases.get(type) ?? type;
    const found = columnTypeRegistry.find(resolved);
    if (found)
        return found;
    if (fallback)
        return fallback;
    // Only reachable before `registerBuiltInColumnTypes()` has run.
    return columnTypeRegistry.get(resolved);
}
export function findColumnType(type) {
    return columnTypeRegistry.find(aliases.get(type) ?? type);
}
export function hasColumnType(type) {
    return columnTypeRegistry.has(aliases.get(type) ?? type);
}
export function registerColumnType(definition) {
    columnTypeRegistry.register(definition);
    return definition;
}
/**
 * The width a column of this type is created with (P-088) and the width a stored `null`
 * renders at (P-022). One table for the api and the web: `columns.create` writes it, and a
 * client falls back to it for the rows created before this shipped.
 *
 * An unregistered type answers the default rather than throwing, like {@link getColumnType}.
 */
export function columnDefaultWidth(type) {
    return findColumnType(type)?.defaultWidth ?? DEFAULT_COLUMN_WIDTH;
}
//# sourceMappingURL=registry.js.map